import type { WritableDeep } from 'type-fest';

import { EMPTY_OBJECT } from '@e-socna/empty';
import type { SectionTypeId } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import type { Getters } from '../../../components/DocumentEditor/types/props.type';
import type { DocumentEditorCmp_RecursiveSection } from '../../../components/DocumentEditor/fragments/recursive-section.fragment';

import { SectionTemplatePage_SectionTemplateVersions_QueryDocument } from '../queries/section-template-versions';

import type { SectionTemplatePage_OneSectionTemplate } from './useFindOneSectionTemplateQuery.hook';

export const useGetters: () => Getters<SectionTemplatePage_OneSectionTemplate> =
  () => {
    const {
      fileDirectoryCategoriesCodeMap,
      printStylesCodeMap,
      sectionTemplateFileCategoriesCodeMap,
      sectionTypesIdMap,
    } = useReferencesContext();

    return {
      editable: ((sectionTemplate: SectionTemplatePage_OneSectionTemplate) => ({
        virtualRoot: sectionTemplate.virtualRoot,
        code: `section-template-${sectionTemplate._id}`,
        title: sectionTemplate.label,
      })) as <W extends boolean = false>(
        editable: W extends false
          ? SectionTemplatePage_OneSectionTemplate
          : WritableDeep<SectionTemplatePage_OneSectionTemplate>,
      ) => Readonly<{
        virtualRoot: W extends false
          ? DocumentEditorCmp_RecursiveSection
          : WritableDeep<DocumentEditorCmp_RecursiveSection>;
        title: string;
        code: string;
      }>,
      printing: ({ printing }: SectionTemplatePage_OneSectionTemplate) => ({
        __typename: 'SectionTemplatePrinting' as const,
        printSettings: {
          __typename: 'PrintSettings' as const,
          addFooters: false,
          addHeaders: false,
          printStyleId: printStylesCodeMap.Blank._id,
        },
        pdfPrints: printing.pdfPrints,
        pdfPrintsIncrement: printing.pdfPrintsIncrement,
      }),
      storage: (sectionTemplate: SectionTemplatePage_OneSectionTemplate) => ({
        directoryCategoryId: fileDirectoryCategoriesCodeMap.SectionTemplate._id,
        entityId: sectionTemplate._id,
        imageCategory: sectionTemplateFileCategoriesCodeMap.TextEditorImages,
        pdfCategory: sectionTemplateFileCategoriesCodeMap.PDFPrints,
      }),
      contentConstraints: (
        sectionTemplate: SectionTemplatePage_OneSectionTemplate,
      ) => {
        const sectionType =
          sectionTypesIdMap[sectionTemplate.typeId as SectionTypeId];

        if (sectionType) {
          return {
            compatibleSectionTypeId: sectionType._id,
            forcePagebreaks: sectionType.forcePagebreaks,
            noSiblings: sectionType.oneChildMax,
            oneChildMax: sectionType.oneChildMax,
          };
        }

        return EMPTY_OBJECT;
      },
      versions: () => ({
        queryDocument:
          SectionTemplatePage_SectionTemplateVersions_QueryDocument,
      }),
    };
  };
